import type { IdentityClient } from "@herefor/contracts";
import { tokenStylesheet } from "@herefor/design-system";
import {
  ACTION_LABELS,
  BRAND,
  CANONICAL_VOCABULARY,
  ERROR_MESSAGES,
  INVITATION_LIFECYCLE_MESSAGES,
  NAVIGATION_LABELS,
  PRIVACY_PAGE_MESSAGES,
} from "@herefor/product-language";
import { useConvex, useConvexAuth, useQuery } from "convex/react";
import {
  Component,
  useEffect,
  useState,
  type ErrorInfo,
  type MouseEvent,
  type ReactNode,
} from "react";

import { SettingsRoute } from "../convex/SettingsRoute";
import { InvitationRoute } from "../convex/InvitationRoute";
import { PrintRoute } from "../convex/PrintRoute";
import { livePlanQuery, type LivePlanResult } from "../convex/plan";
import { AvailabilityRoute, availabilityStylesheet } from "../features/availability";
import { circleSettingsStylesheet } from "../features/circle";
import { calendarFeedStylesheet } from "../features/calendarFeed";
import {
  CoverageRoute,
  coverageStylesheet,
  type NeedCoverageView as CoverageNeedView,
} from "../features/coverage";
import { MyWeekRoute, homeStylesheet } from "../features/home";
import { notificationStylesheet } from "../features/notifications";
import { invitationStylesheet } from "../features/invitations";
import { FirstRunOnboarding, HelpSurface, onboardingStylesheet } from "../features/onboarding";
import { PrivacyRoute, PrivacySettingsLink, privacyStylesheet } from "../features/privacy";
import { ShareSnippet, shareSnippetStylesheet } from "../features/sharing";
import { matchingStylesheet } from "../features/matching";
import { requestStylesheet } from "../features/requests";
import {
  EditEventRoute,
  EventRoute,
  NewEventRoute,
  eventStylesheet,
  type EventAffordances,
} from "../features/events";
import {
  EditNeedRoute,
  NeedRoute,
  NewNeedRoute,
  needStylesheet,
  type NeedAffordances,
} from "../features/needs";
import {
  NeedsAttention,
  PlanWeek,
  planStylesheet,
  toLivePlanViews,
  type NeedView,
} from "../features/plan";
import { PeopleRoute, peopleStylesheet } from "../features/people";
import { RecheckAudienceRoute, RecheckRoute, recheckStylesheet } from "../features/reconfirmation";
import {
  ConnectivityBanner,
  offlineStylesheet,
  startFreshReadConfirmation,
  useConnectivity,
} from "../offline/offline";
import {
  buildPlanNavigationPath,
  parseShellPath,
  type PlanNavigationSurface,
  type PlanSurface,
  type ShellRoute,
} from "../routes/routes";
import { shellStylesheet } from "../styles/shell";

const SHELL_NAVIGATION_EVENT = "herefor:shell-navigation";

const PLAN_NAVIGATION = [
  { label: NAVIGATION_LABELS.today, surface: "today" },
  // SECOND, not last (bead hf-e6-commitments-coverage-udt.9). Today is the family's
  // screen and stays first; the helper's own screen sits immediately beside it,
  // because the adoption bottleneck is the person who gives the app ninety seconds
  // and a screen they have to go hunting for is a screen they open once.
  { label: NAVIGATION_LABELS.myWeek, surface: "myWeek" },
  { label: NAVIGATION_LABELS.calendar, surface: "calendar" },
  { label: NAVIGATION_LABELS.availability, surface: "availability" },
  { label: NAVIGATION_LABELS.people, surface: "people" },
  { label: NAVIGATION_LABELS.settings, surface: "settings" },
  { label: NAVIGATION_LABELS.help, surface: "help" },
] as const satisfies readonly {
  readonly label: string;
  readonly surface: PlanNavigationSurface;
}[];

interface AppProps {
  /** False preserves the static shell when no Convex deployment is configured. */
  readonly live?: boolean;
  /** Used only to reset a denied read when the identity seam changes persona. */
  readonly identityClient?: IdentityClient | null;
}

export function App({ live = false, identityClient = null }: AppProps) {
  const [route, setRoute] = useState<ShellRoute>(readBrowserRoute);
  const [identityEpoch, setIdentityEpoch] = useState(0);

  useEffect(() => {
    const updateRoute = () => {
      setRoute(readBrowserRoute());
    };
    window.addEventListener("popstate", updateRoute);
    window.addEventListener(SHELL_NAVIGATION_EVENT, updateRoute);

    return () => {
      window.removeEventListener("popstate", updateRoute);
      window.removeEventListener(SHELL_NAVIGATION_EVENT, updateRoute);
    };
  }, []);

  useEffect(() => {
    if (identityClient === null) return;
    return identityClient.subscribe(() => {
      setIdentityEpoch((current) => current + 1);
    });
  }, [identityClient]);

  const isPlanRoute = route.kind === "plan";

  return (
    <>
      <style>{tokenStylesheet()}</style>
      <style>{shellStylesheet}</style>
      <style>{planStylesheet}</style>
      <style>{availabilityStylesheet}</style>
      <style>{calendarFeedStylesheet}</style>
      <style>{circleSettingsStylesheet}</style>
      <style>{eventStylesheet}</style>
      <style>{needStylesheet}</style>
      {/* The could-help list has been exporting a stylesheet nobody mounted since
          hf-e5-availability-ccp.2 — registered here alongside the request surfaces it
          now sits with, so the panel on a need detail renders as it was designed. */}
      <style>{matchingStylesheet}</style>
      <style>{requestStylesheet}</style>
      <style>{coverageStylesheet}</style>
      <style>{homeStylesheet}</style>
      <style>{notificationStylesheet}</style>
      <style>{invitationStylesheet}</style>
      <style>{onboardingStylesheet}</style>
      <style>{privacyStylesheet}</style>
      <style>{shareSnippetStylesheet}</style>
      <style>{peopleStylesheet}</style>
      <style>{recheckStylesheet}</style>
      <style>{offlineStylesheet}</style>
      <div className="app-shell">
        <header className="shell-header">
          <div className="shell-header__inner">
            <ShellLink className="shell-brand" href="/plans">
              {BRAND.productName}
            </ShellLink>
          </div>
        </header>

        <div className={`shell-body${isPlanRoute ? " shell-body--with-navigation" : ""}`}>
          {isPlanRoute ? <PlanNavigation route={route} /> : undefined}
          <main className="shell-main" id="main-content" tabIndex={-1}>
            {live && isPlanRoute ? (
              <LiveRouteBoundary
                key={`${String(route.publicPlanRef)}:${String(identityEpoch)}`}
                fallback={<RouteUnavailable />}
              >
                <LivePlanRoute route={route} />
              </LiveRouteBoundary>
            ) : live && route.kind === "invitation" ? (
              <InvitationRoute
                onAccepted={(publicPlanRef) => {
                  navigateTo(`/c/${publicPlanRef}`);
                }}
                token={readInvitationTokenFragment()}
              />
            ) : (
              <RoutePlaceholder route={route} />
            )}
          </main>
        </div>
      </div>
    </>
  );
}

function LivePlanRoute({ route }: { readonly route: Extract<ShellRoute, { kind: "plan" }> }) {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const convex = useConvex();
  const { phase, refreshCycle, confirmReadsFresh } = useConnectivity();
  const publicPlanRef = String(route.publicPlanRef);
  const result = useQuery(livePlanQuery, isAuthenticated ? { publicPlanRef } : "skip");

  useEffect(() => {
    if (!isAuthenticated || phase !== "refreshing") return;
    return startFreshReadConfirmation(
      () => convex.query(livePlanQuery, { publicPlanRef }),
      () => {
        confirmReadsFresh(refreshCycle);
      },
    );
  }, [confirmReadsFresh, convex, isAuthenticated, phase, publicPlanRef, refreshCycle]);

  if (isLoading) {
    return <RoutePlaceholder route={route} loading />;
  }
  if (!isAuthenticated) return <RouteUnavailable />;
  if (result === undefined) return <RoutePlaceholder route={route} loading />;

  return (
    <>
      <ConnectivityBanner />
      <LivePlanSurface result={result} route={route} />
    </>
  );
}

function LivePlanSurface({
  result,
  route,
}: {
  readonly result: LivePlanResult;
  readonly route: Extract<ShellRoute, { kind: "plan" }>;
}) {
  const { actionsPaused } = useConnectivity();
  const views = toLivePlanViews(result);
  // Undefined when this Membership may not share, or when there is nobody to name.
  // The seams below are then not passed at all, so the control is ABSENT rather than
  // present-and-refused (AUT-007).
  const shareNeed = shareSnippetRenderer(result);
  const need =
    route.itemRef === undefined
      ? undefined
      : views.needs.find((candidate) => candidate.id === String(route.itemRef));

  let surface: ReactNode;
  switch (route.surface) {
    case "today":
    case "calendar":
      surface = (
        <>
          <RecheckRoute
            actionsPaused={actionsPaused}
            circleId={result.circleId}
            hideWhenEmpty
            publicPlanRef={String(route.publicPlanRef)}
            timeZone={result.timeZone}
          />
          {result.canAddToPlan ? (
            <ShellLink
              className="shell-action"
              href={`/c/${String(route.publicPlanRef)}/events/new`}
            >
              {ACTION_LABELS.addToPlan}
            </ShellLink>
          ) : null}
          <PlanWeek
            events={views.events}
            heading={result.displayName}
            // A need with no event belongs in the dated list beside the events, and
            // stays there once it is covered (bead hf-qyq). Its only other home is
            // the attention list below, which by design keeps just the states that
            // still want a person — so covering one used to remove it from the app.
            standaloneNeeds={views.standalone}
            {...(shareNeed === undefined
              ? {}
              : { renderNeedAction: (row: NeedView) => shareNeed(fromNeedView(row)) })}
          />
          <NeedsAttention heading={CANONICAL_VOCABULARY.helpNeeded} needs={views.attention} />
          {/* Plan §13.4 item 7, "this week summary": the glance sits AFTER what is
              happening, because Today's job is the day and the week is context for
              it. The full week is its own route (bead hf-e6-commitments-coverage-udt.6). */}
          <CoverageRoute
            circleId={result.circleId}
            publicPlanRef={String(route.publicPlanRef)}
            show="glance"
            timeZone={result.timeZone}
          />
        </>
      );
      break;
    // The week as COVERAGE rather than as events: seven local days, each need's own
    // state and count, and the organiser's chase list in the server's order.
    case "planWeek":
      surface = (
        <CoverageRoute
          circleId={result.circleId}
          publicPlanRef={String(route.publicPlanRef)}
          timeZone={result.timeZone}
          {...(shareNeed === undefined
            ? {}
            : {
                renderNeedAction: (row: CoverageNeedView, dateLabel: string) =>
                  shareNeed(fromCoverageView(row, dateLabel)),
              })}
        />
      );
      break;
    // The helper's own screen, and the two sections Epic 7's notifications deep-link
    // to. One component for all three: they are the same screen with one section
    // lifted to the front, not three pages (bead hf-e6-commitments-coverage-udt.9).
    case "myWeek":
    case "myWeekAsks":
    case "myWeekRechecks":
      surface = (
        <MyWeekRoute
          focus={
            route.surface === "myWeekAsks"
              ? "asks"
              : route.surface === "myWeekRechecks"
                ? "rechecks"
                : null
          }
          result={result}
        />
      );
      break;
    case "event":
      surface =
        route.itemRef === undefined ? (
          <RouteUnavailable />
        ) : (
          <>
            <EventRoute
              circleId={result.circleId}
              timeZone={result.timeZone}
              affordances={eventAffordances(result)}
              eventId={String(route.itemRef)}
              committedPeopleCount={committedPeopleFor(views, String(route.itemRef))}
              linkedNeedCount={cancellableNeedsForEvent(views, String(route.itemRef)).length}
              onEdit={() => {
                navigateTo(
                  `/c/${String(route.publicPlanRef)}/events/${String(route.itemRef)}/edit`,
                );
              }}
              onDone={() => {
                navigateTo(buildPlanNavigationPath(route.publicPlanRef, "calendar"));
              }}
            />
            <NeedsAttention
              heading={CANONICAL_VOCABULARY.helpNeeded}
              needs={needsForEvent(views, String(route.itemRef))}
            />
          </>
        );
      break;
    case "newEvent":
      surface = (
        <NewEventRoute
          circleId={result.circleId}
          timeZone={result.timeZone}
          affordances={eventAffordances(result)}
          onDone={() => {
            navigateTo(buildPlanNavigationPath(route.publicPlanRef, "calendar"));
          }}
        />
      );
      break;
    case "editEvent":
      surface =
        route.itemRef === undefined ? (
          <RouteUnavailable />
        ) : (
          <EditEventRoute
            circleId={result.circleId}
            timeZone={result.timeZone}
            affordances={eventAffordances(result)}
            eventId={String(route.itemRef)}
            onDone={() => {
              navigateTo(`/c/${String(route.publicPlanRef)}/events/${String(route.itemRef)}`);
            }}
          />
        );
      break;
    case "need":
      surface =
        need === undefined || route.itemRef === undefined ? (
          <RouteUnavailable />
        ) : (
          <>
            <NeedRoute
              circleId={result.circleId}
              timeZone={result.timeZone}
              affordances={needAffordances(result)}
              supportNeedId={String(route.itemRef)}
              // COVERAGE IS NOT PASSED DOWN ANY MORE (bead hf-9pw). `NeedRoute`
              // subscribes to `coverage.forNeed` itself, so the browser holds ONE
              // coverage projection instead of two — and the detail can finally say
              // who has been asked, which the plan read has no projection for.
              committedPeopleCount={need.responsible.length}
              // Server-decided, like every other affordance on this model (AUT-007).
              canVolunteer={result.canVolunteer}
              canRecordProxyCommitments={result.canRecordProxyCommitments}
              viewerPersonId={result.viewerPersonId}
              // The OTHERS helping — the viewer is filtered out here, where their own
              // person id is known. A surface that told Ravi "Ravi Blackwood is
              // helping with this" reads as a stranger who happens to share their name.
              responsible={need.responsible
                .filter((person) => String(person.personId) !== result.viewerPersonId)
                .map((person) => ({
                  personId: String(person.personId),
                  name: person.name,
                  recordedByName: person.recordedByName,
                  agreementSource: person.agreementSource,
                }))}
              onViewPlan={() => {
                navigateTo(buildPlanNavigationPath(route.publicPlanRef, "calendar"));
              }}
              onEdit={() => {
                navigateTo(`/c/${String(route.publicPlanRef)}/needs/${String(route.itemRef)}/edit`);
              }}
              onDone={() => {
                navigateTo(buildPlanNavigationPath(route.publicPlanRef, "calendar"));
              }}
            />
            {shareNeed === undefined ? null : shareNeed(fromNeedView(need))}
            <RecheckAudienceRoute
              actionsPaused={actionsPaused}
              // The offline member is asked again like everybody else and will never
              // open the app to answer, so an organiser records what they said (bead
              // hf-ww3). Server-decided, like every other affordance (AUT-007).
              canRecordProxyAnswers={result.canRecordProxyCommitments}
              circleId={result.circleId}
              supportNeedId={String(route.itemRef)}
            />
          </>
        );
      break;
    case "newNeed":
      surface = (
        <NewNeedRoute
          circleId={result.circleId}
          timeZone={result.timeZone}
          affordances={needAffordances(result)}
          // The event is CONTEXT for the window, never its source: the form suggests
          // a wider window and prints the appointment beside it (NED-002).
          eventId={route.itemRef === undefined ? null : String(route.itemRef)}
          onDone={() => {
            navigateTo(buildPlanNavigationPath(route.publicPlanRef, "calendar"));
          }}
        />
      );
      break;
    case "editNeed":
      surface =
        route.itemRef === undefined ? (
          <RouteUnavailable />
        ) : (
          <EditNeedRoute
            circleId={result.circleId}
            timeZone={result.timeZone}
            affordances={needAffordances(result)}
            supportNeedId={String(route.itemRef)}
            onDone={() => {
              navigateTo(`/c/${String(route.publicPlanRef)}/needs/${String(route.itemRef)}`);
            }}
          />
        );
      break;
    case "availability":
    case "availabilityEveryone":
      surface =
        result.availability === null ? <RouteUnavailable /> : <AvailabilityRoute result={result} />;
      break;
    // The member's own calendar link (CAL-007, bead hf-7o6). It is the whole of the
    // control over a bearer URL that does not expire, so it is a real surface rather
    // than a placeholder — see apps/web/src/features/calendarFeed.
    case "settings":
      surface = (
        <>
          <SettingsRoute circleId={result.circleId} />
          <PrivacySettingsLink href={`/c/${String(route.publicPlanRef)}/privacy`} />
        </>
      );
      break;
    case "people":
      surface = <PeopleRoute circleId={result.circleId} />;
      break;
    case "print":
      surface = <PrintRoute result={result} />;
      break;
    case "privacy":
      surface = (
        <PrivacyRoute circleId={result.circleId} publicPlanRef={String(route.publicPlanRef)} />
      );
      break;
    case "help":
      surface = (
        <>
          <FirstRunOnboarding
            forceReplay
            onStartHelper={() => {
              navigateTo(buildPlanNavigationPath(route.publicPlanRef, "availability"));
            }}
            onStartOrganiser={() => {
              navigateTo(`/c/${String(route.publicPlanRef)}/events/new`);
            }}
            result={result}
          />
          <HelpSurface privacyHref={`/c/${String(route.publicPlanRef)}/privacy`} />
        </>
      );
      break;
  }

  return (
    <div className="shell-panel" data-live-plan="ready">
      {route.surface === "help" || route.surface === "privacy" ? null : (
        <FirstRunOnboarding
          onStartHelper={() => {
            navigateTo(buildPlanNavigationPath(route.publicPlanRef, "availability"));
          }}
          onStartOrganiser={() => {
            navigateTo(`/c/${String(route.publicPlanRef)}/events/new`);
          }}
          result={result}
        />
      )}
      {surface}
    </div>
  );
}

/**
 * One need, in the shape the share snippet needs it — whichever surface it came from.
 *
 * The plan week and the coverage week model a need differently, and neither shape is
 * wrong; this is the small translation that lets ONE gate serve both rather than two
 * copies of the same three conditions drifting apart.
 */
interface ShareableNeed {
  readonly supportNeedId: string;
  readonly stateKey: NeedView["stateKey"];
  readonly dateLabel: string;
  readonly windowLabel: string;
}

/**
 * The organiser's share snippet, or nothing at all (SHA-001–SHA-012, plan §18;
 * bead hf-e8-sharing-export-x1v.5).
 *
 * FAIL-CLOSED THREE TIMES, and each condition answers a different question:
 *
 *   `canSharePlan` — may this Membership share at all? The SERVER's answer, derived
 *        from `plan.share`, never inferred here from another affordance (AUT-007).
 *   `supportedPerson` — is there somebody this plan is for? The one record carries
 *        both the chosen name and D-091's confirmation flag. A confirmed spelling may
 *        supply the discreet first name; a placeholder supplies only neutral wording.
 *   `needsSomeone` — is this an INVITATION? Only a need that still wants a person is.
 *        Sharing a covered need would ask a wider circle for help nobody needs, and
 *        sharing a cancelled one would ask for help with something not happening.
 *
 * Undefined rather than a renderer that returns null: the seams take an optional
 * prop, so a Membership without `plan.share` never has the control in the DOM.
 */
function shareSnippetRenderer(
  result: LivePlanResult,
): ((need: ShareableNeed) => ReactNode) | undefined {
  const supportedPerson = result.supportedPerson;
  if (!result.canSharePlan || supportedPerson === null) return undefined;

  return (need) =>
    need.stateKey !== "needsSomeone" ? null : (
      <ShareSnippet
        dateLabel={need.dateLabel}
        publicPlanRef={result.publicPlanRef}
        supportNeedId={need.supportNeedId}
        supportedPersonIsPlaceholder={supportedPerson.isPlaceholder}
        supportedPersonName={supportedPerson.chosenName}
        windowLabel={need.windowLabel}
      />
    );
}

function fromNeedView(need: NeedView): ShareableNeed {
  return {
    supportNeedId: need.id,
    stateKey: need.stateKey,
    dateLabel: need.dateLabel,
    windowLabel: need.windowLabel,
  };
}

/** The coverage week carries the day's label beside the need rather than on it. */
function fromCoverageView(need: CoverageNeedView, dateLabel: string): ShareableNeed {
  return {
    supportNeedId: need.supportNeedId,
    stateKey: need.stateKey,
    dateLabel,
    windowLabel: need.windowLabel,
  };
}

/**
 * What the server says this person may do to an event.
 *
 * Draft deletion uses the same server-authorized `event.cancel` capability as
 * cancellation; the browser never infers either permission from another affordance.
 */
function eventAffordances(result: LivePlanResult): EventAffordances {
  return {
    canEdit: result.canEditEvents,
    canCancel: result.canCancelEvents,
    canDeleteDraft: result.canCancelEvents,
  };
}

/**
 * What the server says this person may do to a support need.
 *
 * Same fail-closed arrangement as `eventAffordances`: the browser consumes dedicated
 * capability-derived flags and never infers them from add-to-plan access.
 */
function needAffordances(result: LivePlanResult): NeedAffordances {
  return {
    canEdit: result.canEditNeeds,
    canCancel: result.canCancelNeeds,
    canDeleteDraft: result.canCancelNeeds,
    // `request.send`, not need-editing (bead hf-e6-commitments-coverage-udt.1). The
    // could-help surface borrowed `canEditNeeds` while the Direct Request mutation was
    // being built; this is its own server-decided flag, which is what AUT-007 asks for.
    canSendRequests: result.canSendRequests,
    // `people.view`, its own server-decided flag (bead hf-mtk). The ask-anyone list
    // reads the people directory, and asking is not the same privilege as reading it.
    canViewPeople: result.canViewPeople,
    // `availability.view`, the capability the could-help list is built from.
    canViewAvailability: result.canViewAvailability,
  };
}

/** How many people have agreed to help with the needs linked to one event. */
function committedPeopleFor(views: ReturnType<typeof toLivePlanViews>, eventId: string): number {
  return needsForEvent(views, eventId).reduce((total, need) => total + need.responsible.length, 0);
}

/** Support needs keep their own windows; the event route composes rather than derives them. */
function needsForEvent(views: ReturnType<typeof toLivePlanViews>, eventId: string) {
  return views.events.find((candidate) => candidate.id === eventId)?.needs ?? [];
}

/** The event dialog counts only needs this act will newly cancel, not terminal history. */
function cancellableNeedsForEvent(views: ReturnType<typeof toLivePlanViews>, eventId: string) {
  return needsForEvent(views, eventId).filter(
    (need) => need.stateKey !== "done" && need.stateKey !== "cancelled",
  );
}

/** Push a shell route without a full navigation, exactly as ShellLink does. */
function navigateTo(href: string): void {
  window.history.pushState(null, "", href);
  window.dispatchEvent(new Event(SHELL_NAVIGATION_EVENT));
}

class LiveRouteBoundary extends Component<
  { readonly children: ReactNode; readonly fallback: ReactNode },
  { readonly denied: boolean }
> {
  override state = { denied: false };

  static getDerivedStateFromError(): { readonly denied: true } {
    return { denied: true };
  }

  override componentDidCatch(_error: unknown, _info: ErrorInfo): void {
    // Boundary errors may contain private server details. The UI intentionally
    // renders only the governed route-unavailable message and logs nothing.
  }

  override render(): ReactNode {
    return this.state.denied ? this.props.fallback : this.props.children;
  }
}

function PlanNavigation({ route }: { readonly route: Extract<ShellRoute, { kind: "plan" }> }) {
  return (
    <nav>
      <ul className="shell-navigation">
        {PLAN_NAVIGATION.map((item) => (
          <li key={item.surface}>
            <ShellLink
              ariaCurrent={isCurrentSurface(route.surface, item.surface) ? "page" : undefined}
              className="shell-navigation__link"
              href={buildPlanNavigationPath(route.publicPlanRef, item.surface)}
            >
              {item.label}
            </ShellLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function RoutePlaceholder({
  route,
  loading = false,
}: {
  readonly route: ShellRoute;
  readonly loading?: boolean;
}) {
  if (route.kind === "notFound") {
    return <RouteUnavailable />;
  }

  return (
    <section
      aria-busy={loading || undefined}
      className="shell-panel"
      data-shell-route={routeKey(route)}
    >
      <h1>{routeHeading(route)}</h1>
      <div aria-hidden="true" className="shell-placeholder">
        <span className="shell-placeholder__line" />
        <span className="shell-placeholder__card" />
        <span className="shell-placeholder__card" />
      </div>
    </section>
  );
}

function RouteUnavailable() {
  return (
    <section className="shell-panel shell-error" data-shell-route="notFound">
      <h1>{ERROR_MESSAGES.routeUnavailable.title}</h1>
      <p>{ERROR_MESSAGES.routeUnavailable.body}</p>
      <ShellLink className="shell-action" href="/plans">
        {ERROR_MESSAGES.routeUnavailable.action}
      </ShellLink>
    </section>
  );
}

interface ShellLinkProps {
  readonly ariaCurrent?: "page" | undefined;
  readonly children: ReactNode;
  readonly className: string;
  readonly href: string;
}

function ShellLink({ ariaCurrent, children, className, href }: ShellLinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    window.history.pushState(null, "", href);
    window.dispatchEvent(new Event(SHELL_NAVIGATION_EVENT));
    window.scrollTo({ top: 0 });
    queueMicrotask(() => document.getElementById("main-content")?.focus());
  };

  return (
    <a aria-current={ariaCurrent} className={className} href={href} onClick={handleClick}>
      {children}
    </a>
  );
}

function readBrowserRoute(): ShellRoute {
  const invitationRoute = window.location.pathname === "/join";
  if (window.location.search.length > 0 || (!invitationRoute && window.location.hash.length > 0)) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${invitationRoute ? window.location.hash : ""}`,
    );
  }

  return parseShellPath(window.location.pathname);
}

/** Fragment transport keeps the one-time token out of request logs and referrers. */
function readInvitationTokenFragment(): string | null {
  if (window.location.pathname !== "/join") return null;
  const value = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
  return value.length > 0 ? value : null;
}

function isCurrentSurface(current: PlanSurface, navigation: PlanNavigationSurface): boolean {
  switch (navigation) {
    case "today":
      return current === "today";
    case "myWeek":
      return ["myWeek", "myWeekAsks", "myWeekRechecks"].includes(current);
    case "calendar":
      return [
        "calendar",
        "newEvent",
        "event",
        "editEvent",
        "newNeed",
        "need",
        "editNeed",
        "planWeek",
        "print",
      ].includes(current);
    case "availability":
      return current === "availability" || current === "availabilityEveryone";
    case "people":
      return current === "people";
    case "settings":
      return current === "settings";
    case "help":
      return current === "help";
  }
}

function routeHeading(route: Exclude<ShellRoute, { kind: "notFound" }>): string {
  switch (route.kind) {
    case "entry":
      return BRAND.productName;
    case "plans":
      return BRAND.yourPlans;
    case "invitation":
      return INVITATION_LIFECYCLE_MESSAGES.landing.heading;
    case "plan":
      return planSurfaceHeading(route.surface);
  }
}

function planSurfaceHeading(surface: PlanSurface): string {
  if (surface === "today") return NAVIGATION_LABELS.today;
  if (surface === "myWeek" || surface === "myWeekAsks" || surface === "myWeekRechecks") {
    return NAVIGATION_LABELS.myWeek;
  }
  if (surface === "availability" || surface === "availabilityEveryone") {
    return NAVIGATION_LABELS.availability;
  }
  if (surface === "people") return NAVIGATION_LABELS.people;
  if (surface === "settings") return NAVIGATION_LABELS.settings;
  if (surface === "help") return NAVIGATION_LABELS.help;
  if (surface === "privacy") return PRIVACY_PAGE_MESSAGES.heading;
  return NAVIGATION_LABELS.calendar;
}

function routeKey(route: Exclude<ShellRoute, { kind: "notFound" }>): string {
  return route.kind === "plan" ? route.surface : route.kind;
}
